import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import DOM from '../../../common/DOM';
import './tooltip.scss';

let _tooltips = [];

const removeItemFromArray = (array, item) => {
    return array.filter((a) => a !== item);
};

export default class Tooltip extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = { visible: false, className: '', tooltip: null };

        this.el = document.createElement('div');
        this.el.className = `tooltip-container ${props.className || ''} ${this.state.className}`;
        this.el.tabIndex = 0;
        this.popupRoot =
            document.getElementById('child') ||
            document.getElementsByClassName('widget-root')[0] ||
            document.getElementsByTagName('body');
        this.anchorTop = null;
        this.anchorBottom = null;
        this.currentTarget = null;
        this.targetArray = null;

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onWindowResize = this.onWindowResize.bind(this);
        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.subscribed = false;

        this.hideTimeout = null;
        this.showTimeout = null;
    }

    static getDerivedStateFromProps(newProps) {
        return {
            className: newProps.visible ? 'visible' : ''
        };
    }

    componentDidMount() {
        _tooltips.push(this);

        this.popupRoot.appendChild(this.el);
        this.attachToElements();
    }

    componentWillUnmount() {
        this.disposed = true;
        this.detachFromElements();
        this.popupRoot.removeChild(this.el);

        _tooltips = removeItemFromArray(_tooltips, this);
    }

    attachEvents() {
        if (!this.subscribed) {
            this.subscribed = true;
            document.addEventListener('touchstart', this.onDocumentClick, true);
            document.addEventListener('click', this.onDocumentClick, true);
            window.addEventListener('resize', this.onWindowResize, true);
        }
    }

    detachEvents() {
        if (this.subscribed) {
            this.subscribed = false;
            document.removeEventListener('click', this.onDocumentClick, true);
            document.removeEventListener('touchstart', this.onDocumentClick, true);
            window.addEventListener('resize', this.onWindowResize, true);
        }
    }

    attachToElements() {
        let selector = '[data-tip]';
        if (this.props.id) {
            selector = `[data-tip='${this.props.id}']`;
        }
        this.targetArray = document.querySelectorAll(selector);
        this.targetArray.forEach((el) => {
            switch (this.props.trigger) {
                case 'hover':
                    el.addEventListener('touchstart', this.onMouseEnter); // mouse enter is not available on mobile, so touchstart is enabled here
                    el.addEventListener('mouseenter', this.onMouseEnter); // mouse enter for desktops
                    el.addEventListener('mouseleave', this.onMouseLeave); // mouse enter for desktops
                    break;
                case 'click':
                    el.addEventListener('click', this.onMouseEnter);
                    break;
                case 'manual':
                    break;
            }
        });
    }

    detachFromElements() {
        if (this.targetArray) {
            this.targetArray.forEach((el) => {
                switch (this.props.trigger) {
                    case 'hover':
                        el.removeEventListener('touchstart', this.onMouseEnter);
                        el.removeEventListener('mouseenter', this.onMouseEnter);
                        el.removeEventListener('mouseleave', this.onMouseLeave);
                        break;
                    case 'click':
                        el.removeEventListener('click', this.onMouseEnter);
                        break;
                    case 'manual':
                        break;
                }
            });
            this.targetArray = null;
        }
    }

    rebuild() {
        this.detachFromElements();
        this.attachToElements();
    }

    onMouseEnter(e) {
        this.show(e.target);
    }

    onMouseLeave() {
        this.hide();
    }

    getAnchor() {
        return this.props.anchor === 'auto' ? 'below' : this.props.anchor;
    }

    getAlign() {
        return this.props.align === 'auto' ? 'left' : this.props.align;
    }

    static show(target) {
        _tooltips.forEach((tooltip) => {
            tooltip.targetArray.forEach((el) => {
                if (el === target || el.dataset['tip'] === target.dataset['tip']) {
                    tooltip.show(el);
                }
            });
        });
    }

    static hide() {
        _tooltips.forEach((tooltip) => {
            tooltip.hide();
        });
    }

    show(target = null) {
        if (!target) {
            target = this.targetArray[0] || null;
        }
        if (this.props.trigger === 'click' && this.state.visible) {
            Tooltip.hide();
            return;
        }
        if (!target || (target && this.currentTarget === target && this.state.visible)) {
            return;
        }

        const showNow = () => {
            Tooltip.hide();

            let content = null;
            if (this.props.content) {
                content = this.props.content();
            } else if (this.props.children) {
                content = this.props.children;
            } else if (target.dataset['tiptext'] && target.dataset['tiptext'].trim() !== '') {
                content = <p>{target.dataset['tiptext']}</p>;
            }

            if (!content) {
                return; // no content
            }

            this.currentTarget = target;

            const anchor = this.getAnchor();
            this.setState({
                visible: true,
                className: '',
                tooltip: ReactDOM.createPortal(
                    <div>
                        {(anchor === 'below' || anchor === 'top') && (
                            <div ref={(e) => (this.anchorTop = e)} className="arrowtop" />
                        )}
                        {content}
                        {(anchor === 'above' || anchor === 'bottom') && (
                            <div ref={(e) => (this.anchorBottom = e)} className="arrowbottom" />
                        )}
                    </div>,
                    this.el
                )
            });
            this.el.style.cssText = this.getCSSStyle(target);
            // poniższe jest potrzebne, do ponownego przeliczenia pozycji, bo czasami po wyświetleniu i tak jest zła pozycja
            setTimeout(() => {
                this.el.style.cssText = this.getCSSStyle(target);
            }, 1);

            if (this.props.hideDelay > 0) {
                if (this.hideTimeout) clearTimeout(this.hideTimeout);
                this.hideTimeout = setTimeout(() => {
                    this.hide();
                    clearTimeout(this.hideTimeout);
                }, this.props.hideDelay);
            }
            this.attachEvents();
        };

        if (this.showTimeout) clearTimeout(this.showTimeout);
        if (this.props.showDelay > 0) {
            this.showTimeout = setTimeout(() => {
                showNow();
                clearTimeout(this.showTimeout);
            }, this.props.showDelay);
        } else {
            showNow();
        }
    }

    hide() {
        if (this.state.visible) {
            this.currentTarget = null;
            this.setState({
                visible: false,
                className: '',
                tooltip: null
            });
            this.detachEvents();
        }
    }

    onDocumentClick(e) {
        if (this.disposed) return;

        if (e.target === this.currentTarget) return;

        if (this.el !== e.target && !this.el.contains(e.target)) {
            this.hide();
            if (this.props.onHide) {
                this.props.onHide(e);
            }
        }
    }

    onWindowResize() {
        if (this.disposed) return;

        if (this.currentTarget && this.el) {
            this.el.style.cssText = this.getCSSStyle(this.currentTarget);
        }
    }

    getArrowElement() {
        const anchor = this.getAnchor();
        return anchor === 'below' || anchor === 'top' ? this.anchorTop : this.anchorBottom;
    }

    getCSSStyle(target) {
        let x = 0,
            y = 0,
            css = '';
        if (target) {
            const pos = DOM.getElementPosition(target);
            if (pos) {
                let tipSize = DOM.getElementSize(this.el);
                const anchor = this.getAnchor();

                const targetSize = DOM.getElementSize(target);
                const align = this.getAlign();
                const rootSize = DOM.getDocumentSize(this.popupRoot);
                const calcX = () => {
                    let xx;
                    switch (align) {
                        case 'left':
                            xx = Math.max(0, Math.ceil(pos.x));
                            break;
                        case 'center':
                            xx = Math.max(
                                0,
                                Math.ceil(pos.x - (tipSize.width / 2 - targetSize.width / 2))
                            );
                            break;
                        default:
                            xx = 0;
                            break;
                    }
                    if (xx + tipSize.width > rootSize.width) {
                        xx = rootSize.width - tipSize.width;
                    }
                    return Math.max(0, xx);
                };

                const arrowElement = this.getArrowElement();
                const arrowSize = DOM.getElementSize(arrowElement);
                const calcY = () => {
                    switch (anchor) {
                        case 'below':
                        case 'top':
                            return Math.max(
                                0,
                                Math.ceil(pos.y + targetSize.height + arrowSize.height / 2)
                            );
                        case 'above':
                        case 'bottom':
                            return Math.max(
                                0,
                                Math.ceil(pos.y - tipSize.height - arrowSize.height / 2)
                            );
                        default:
                            return 0;
                    }
                };

                x = calcX();
                y = calcY();

                const targetPosition = DOM.getElementPosition(target);
                if (arrowElement) {
                    if (
                        anchor === 'below' ||
                        anchor === 'top' ||
                        anchor === 'above' ||
                        anchor === 'bottom'
                    ) {
                        if (align === 'left' || targetSize.width > tipSize.width) {
                            const offset = targetSize.width < 15 ? targetSize.width / 2 : 15;
                            const ax = Math.max(
                                arrowSize.width + offset > tipSize.width
                                    ? tipSize.width / 2 - arrowSize.width / 2
                                    : offset,
                                targetPosition.x - x + 15
                            );
                            arrowElement.style.cssText = `left: ${ax}px;`;
                        } else {
                            const ax =
                                targetPosition.x - x - (arrowSize.width / 2 - targetSize.width / 2);
                            arrowElement.style.cssText = `left: ${ax}px`;
                        }
                    }
                }
            }
        }
        return `left: ${x}px; top: ${y}px; ${this.props.style || ''} ${css}`;
    }

    render() {
        this.el.className = `tooltip-container ${this.props.className || ''} ${
            this.state.className
        } ${this.state.visible ? 'visible' : ''}`;
        this.el.id = this.props.id;

        return this.state.tooltip;
    }
}

// This part is commented inentionally as it documents attributes on target elements.
// Target.defaultProps = {
//     'data-tip': PropTypes.string, // name of associated Tooltip Element.
//     'data-tiptext': PropTypes.string // tooltip text to be shown
// };

Tooltip.defaultProps = {
    id: null, // tooltip name (associated to data-tip on target element)
    className: '', // additional class'es for container
    trigger: 'hover', // show method: hover, click, manual
    anchor: 'auto', // tip positioning relative to target: auto, above|bottom, below|top
    align: 'left', // tip alignment relative to target: auto, left, center
    hideDelay: 0, // timeout after hide tooltip
    showDelay: 0, // timeout after tooltip will show
    style: '' // additional styles for tooltip
};

Tooltip.propTypes = {
    id: PropTypes.string,
    onHide: PropTypes.func,
    onShow: PropTypes.func,
    content: PropTypes.func,
    className: PropTypes.string,
    children: PropTypes.element,
    trigger: PropTypes.string,
    anchor: PropTypes.string,
    align: PropTypes.string,
    hideDelay: PropTypes.number,
    showDelay: PropTypes.number,
    style: PropTypes.string
};
